import axios from "axios";
import {
  calculateKcalAndPercentage,
  getMealDescription,
  getMealEntriesString,
} from "../../../helpers/mealsHelper";
import i18n from "../../../i18n";
import { handleHttpErrors } from "../../../modules/http";
import { getClaims } from "../../../modules/login";
import { canShowStudyDrugMenuItem } from "../../../modules/utils";
import {
  orderType,
  tablePageSizes,
} from "../../ui-library/CustomTable/constants";

// Constants
const LOADING_FAVORITE_MEALS = "LOADING_FAVORITE_MEALS";
const SET_FAVORITE_MEALS = "SET_FAVORITE_MEALS";
const RESET_STATE = "RESET_STATE";
const LOGOUT = "LOGOUT";
const SET_DIET_DIARY_MENU_ITEMS = "SET_DIET_DIARY_MENU_ITEMS";

// Acessors
const getFavoriteMeals = (state) => state?.favoriteMeals;
const getTotalItems = (state) => state?.totalItems;
const getSearchedParams = (state) => state?.searchedParams;
const getLoading = (state) => state?.loading;
const getDietMenuItems = (state) => state?.dietMenuItems;
const getStudies = (state) => state?.loginReducer?.studies;

const canEditFavorites = (state) => getClaims(state, "favorites.canEdit");
const canViewDetailFavorites = (state) =>
  getClaims(state, "favorites.detail.canView");
const canCreateFavorites = (state) => getClaims(state, "favorites.canCreate");
const canRemoveFavorites = (state) => getClaims(state, "favorites.canRemove");

// Default state
const initialState = {
  favoriteMeals: [],
  totalItems: null,
  loading: false,
  dietMenuItems: null,
  searchedParams: {
    page: 0,
    limit: tablePageSizes[0],
    search: "",
    orderBy: "date",
    order: orderType.DESC,
  },
};

export const favoritesReducer = (state = initialState, action) => {
  switch (action.type) {
    case LOADING_FAVORITE_MEALS:
      return {
        ...state,
        loading: true,
      };
    case SET_FAVORITE_MEALS:
      const { meals, records } = action;
      return {
        ...state,
        favoriteMeals: meals,
        totalItems: records,
        searchedParams: action.searchedParams,
        loading: false,
      };
    case SET_DIET_DIARY_MENU_ITEMS: {
      const { dietMenuItems } = action;

      return Object.assign({}, state, {
        dietMenuItems: dietMenuItems,
      });
    }
    case RESET_STATE:
      return initialState;
    case LOGOUT:
      return initialState;
    default:
      return state;
  }
};

const resetState = () => (dispatch) => {
  dispatch({ type: RESET_STATE });
};

const fetchFavoriteMeals = (searchedParams) => (dispatch) => {
  const params = {
    ...searchedParams,
  };

  dispatch({
    type: LOADING_FAVORITE_MEALS,
  });

  return axios
    .get(`/api/diet/meals/favorites`, { params })
    .then(({ data }) => {
      dispatch({
        type: SET_FAVORITE_MEALS,
        meals: parseTableData(data.data),
        records: data.records,
        searchedParams,
      });
    })
    .catch((error) => {
      return handleHttpErrors(error.request?.status, {
        default: i18n.t("errorText.dietDiary.favorites.get"),
      });
    });
};

const parseTableData = (data) => {
  const formattedData = [];
  if (data) {
    data.forEach((elem) => {
      const { name, mealJson, key, reasonForChange, createdAt, isOriginator } =
        elem;
      const meal = JSON.parse(mealJson);

      const favoriteObj = {
        name: name,
        description: getMealDescription(meal.items),
        entriesString: getMealEntriesString(meal),
        calories: { ...meal.aboutMeal.Calories },
        protein: { ...meal.aboutMeal.Protein },
        carbohydrates: { ...meal.aboutMeal.Carbs },
        mediumChainFats: { ...meal.aboutMeal.MediumChainFats },
        longChainFats: { ...meal.aboutMeal.LongChainFats },
        json: mealJson,
        dateTime: createdAt,
        favoriteId: key,
        reasonForChange: reasonForChange,
        isOriginator: isOriginator,
      };

      formattedData.push(calculateKcalAndPercentage(favoriteObj));
    });
  }

  return formattedData;
};

const fetchDietDiaryMenuItems = () => (dispatch, getState) => {
  if (!canShowStudyDrugMenuItem(getStudies(getState())[0].status)) {
    return;
  }

  return axios.get(`/api/dietmenu`).then(({ data }) => {
    dispatch({ type: SET_DIET_DIARY_MENU_ITEMS, dietMenuItems: data });
  });
};

const deleteFavoriteMeal = (favoriteMealId) => (dispatch) => {
  const params = {
    mealID: favoriteMealId,
  };

  return axios
    .delete(`/api/diet/meals/favorites`, { params })
    .then(() => {
      return Promise.resolve(i18n.t("appText.favorites.remove.success"));
    })
    .catch((error) => {
      return handleHttpErrors(error.request?.status, {
        default: i18n.t("errorText.favorites.remove.error"),
      });
    });
};

export const mapStateToProps = (state) => {
  return {
    favoriteMeals: getFavoriteMeals(state),
    totalItems: getTotalItems(state),
    searchedParams: getSearchedParams(state),
    canEditFavorites: canEditFavorites(state),
    canViewDetailFavorites: canViewDetailFavorites(state),
    canCreateFavorites: canCreateFavorites(state),
    loading: getLoading(state),
    dietMenuItems: getDietMenuItems(state),
    canRemoveFavorites: canRemoveFavorites(state),
  };
};

export const mapDispatchToProps = {
  fetchFavoriteMeals,
  resetState,
  fetchDietDiaryMenuItems,
  deleteFavoriteMeal,
};
